"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  step: "Fase 01",
  title: "Recolección y Filtrado",
  description: "El proceso comienza con la recuperación de aceites de cocina usados y biomasa en toda la isla. Estos materiales se someten a un filtrado industrial riguroso para eliminar impurezas sólidas y residuos de agua, asegurando una materia prima pura lista para la transformación química en nuestra planta de procesamiento.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/oil_collection_filtration_cuba.png",
  imageAlt: "Proceso de filtrado de aceite vegetal usado en BioPower",
  imageLeft: true
}, {
  step: "Fase 02",
  title: "Transesterificación",
  description: "Mediante una reacción química controlada, los triglicéridos del aceite reaccionan con alcohol para separar la glicerina del biodiesel. Este paso técnico es el corazón de nuestra producción, donde la energía orgánica se convierte en un combustible líquido de alta eficiencia que reduce drásticamente las emisiones de CO2.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/biodiesel_chemical_reaction_lab.png",
  imageAlt: "Reacción química de transesterificación en laboratorio",
  imageLeft: false
}, {
  step: "Fase 03",
  title: "Purificación y Entrega",
  description: "El biodiesel final se lava y se seca para alcanzar los estándares internacionales de calidad. Este combustible limpio está listo para alimentar motores diesel en Cuba, eliminando aproximadamente 2.5 kg de CO2 por cada galón producido y reduciendo la dependencia nacional de los combustibles fósiles importados.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/pure_biodiesel_fuel_tank.png",
  imageAlt: "Biodiesel puro listo para distribución en Cuba",
  imageLeft: true
}];
function StepBlock({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div data-index={index} ref={ref} className={`grid grid-cols-1 md:grid-cols-2 gap-0 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className={`overflow-hidden ${step.imageLeft ? "md:order-1" : "md:order-2"} order-1`}>
        <img src={step.image} alt={step.imageAlt} className="w-full h-full object-cover aspect-square md:aspect-auto md:min-h-[480px] transition-transform duration-700 hover:scale-105" />
      </div>

      {}
      <div className={`flex flex-col justify-center px-8 py-14 md:px-14 md:py-20 bg-card shadow-md hover:shadow-xl transition-all duration-300 rounded-lg border border-border/50 ${step.imageLeft ? "md:order-2" : "md:order-1"} order-2`}>
        <span className="text-xs font-default font-semibold tracking-widest uppercase mb-3" style={{
        color: "var(--chart-2)"
      }}>
          {step.step}
        </span>
        <h3 className="font-default text-2xl md:text-3xl font-bold mb-5 leading-tight tracking-tight" style={{
        color: "var(--foreground)"
      }}>
          {step.title}
        </h3>
        <div className="w-10 h-px mb-6" style={{
        backgroundColor: "var(--chart-3)"
      }} />
        <p className="font-default text-base leading-relaxed" style={{
        color: "var(--muted-foreground)"
      }}>
          {step.description}
        </p>
      </div>
    </div>;
}
export default function FromSeedToTank() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="232418" id="from-seed-to-tank" className="bg-background text-foreground py-16 md:py-24">
      {}
      <div ref={headerRef} className={`max-w-3xl mx-auto px-6 text-center mb-16 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <span className="block text-xs font-default font-semibold tracking-widest uppercase mb-4" style={{
        color: "var(--chart-1)"
      }}>
          Energía para Cuba
        </span>
        <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold mb-5 tracking-tighter" style={{
        color: "var(--foreground)"
      }}>
          Del Residuo al Tanque
        </h2>
        <p className="font-default text-base md:text-lg leading-relaxed" style={{
        color: "var(--muted-foreground)"
      }}>
          Descubra cómo BioPower transforma el aceite usado y la biomasa local en combustible renovable, impulsando la soberanía energética de nuestra nación.
        </p>
        <div className="mx-auto mt-8 w-16 h-px" style={{
        backgroundColor: "var(--chart-1)"
      }} />
      </div>

      {}
      <div className="max-w-7xl mx-auto px-0 md:px-6 flex flex-col gap-4 md:gap-6">
        {steps.map((step, i) => <StepBlock key={step.step} step={step} index={i} />)}
      </div>

      {}
      <div className="flex justify-center gap-2 mt-16">
        {[0, 1, 2, 3, 4].map((dot, i) => <span data-index={i} key={dot} className="inline-block w-1.5 h-1.5 rounded-lg" style={{
        backgroundColor: i === 2 ? "var(--chart-1)" : "var(--border)",
        opacity: i === 2 ? 1 : 0.5
      }} />)}
      </div>
    </section>;
}